/**
 * Widget styles, injected into the shadow root (constructable stylesheet
 * with a <style> fallback). Everything themeable is a --dtro-* custom
 * property the host page (or config.theme) can override on the host element.
 */
export const WIDGET_CSS = /* css */ `
:host {
  --dtro-accent: #4f46e5;
  --dtro-accent-contrast: #ffffff;
  --dtro-bg: #ffffff;
  --dtro-fg: #111827;
  --dtro-muted: #6b7280;
  --dtro-border: #e5e7eb;
  --dtro-bubble-participant: var(--dtro-accent);
  --dtro-bubble-participant-fg: var(--dtro-accent-contrast);
  --dtro-bubble-interviewer: #f3f4f6;
  --dtro-bubble-interviewer-fg: var(--dtro-fg);
  --dtro-line: var(--dtro-border);
  --dtro-radius: 14px;
  --dtro-radius-bubble: 12px;
  --dtro-radius-pill: 999px;
  --dtro-font: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  --dtro-font-display: var(--dtro-font);
  --dtro-font-weight: 400;
  --dtro-heading-weight: 600;
  --dtro-z: 2147483000;
  --dtro-panel-width: 380px;
  --dtro-panel-height: 600px;
  all: initial;
  font-family: var(--dtro-font);
  font-weight: var(--dtro-font-weight);
}
/* The scheme is the widget's call (src/theme.ts resolveScheme): the study's
   default mode, else the visitor's device. */
:host([data-dtro-scheme="dark"]) {
  --dtro-bg: #1f2937;
  --dtro-fg: #f9fafb;
  --dtro-muted: #9ca3af;
  --dtro-border: #374151;
  --dtro-bubble-interviewer: #374151;
}
* { box-sizing: border-box; }
/* Every rule below that sets display on an element we toggle with the
   hidden property (the splash and all of its parts) would otherwise beat the
   browser's own [hidden] rule — author styles outrank the UA sheet whatever
   the specificity — leaving a hidden element on screen. That is not a
   hypothetical: an always-visible splash covered the whole thread. */
[hidden] { display: none !important; }
button { font: inherit; cursor: pointer; }
button:focus-visible, select:focus-visible, textarea:focus-visible, input:focus-visible {
  outline: 2px solid var(--dtro-accent);
  outline-offset: 2px;
}

.dtro-root { position: fixed; bottom: 20px; z-index: var(--dtro-z); }
.dtro-root.bottom-right { right: 20px; }
.dtro-root.bottom-left { left: 20px; }
.dtro-root.inline { position: static; width: 100%; height: 100%; }

.dtro-bubble {
  width: 56px; height: 56px; border-radius: 50%;
  border: none; background: var(--dtro-accent); color: var(--dtro-accent-contrast);
  box-shadow: 0 4px 16px rgba(0,0,0,.24);
  display: flex; align-items: center; justify-content: center;
  transition: transform .15s ease;
}
.dtro-bubble:hover { transform: scale(1.06); }
.dtro-bubble svg { width: 26px; height: 26px; }

.dtro-panel {
  position: absolute; bottom: 72px;
  width: var(--dtro-panel-width); height: var(--dtro-panel-height);
  max-height: calc(100vh - 110px); max-width: calc(100vw - 40px);
  background: var(--dtro-bg); color: var(--dtro-fg);
  border: 1px solid var(--dtro-border); border-radius: var(--dtro-radius);
  box-shadow: 0 12px 40px rgba(0,0,0,.28);
  display: flex; flex-direction: column; overflow: hidden;
  opacity: 0; transform: translateY(8px); pointer-events: none;
  transition: opacity .18s ease, transform .18s ease;
}
.dtro-root.bottom-right .dtro-panel { right: 0; }
.dtro-root.bottom-left .dtro-panel { left: 0; }
.dtro-root.inline .dtro-panel {
  position: static; width: 100%; height: 100%; max-height: none;
  opacity: 1; transform: none; pointer-events: auto; box-shadow: none;
}
.dtro-panel.open { opacity: 1; transform: translateY(0); pointer-events: auto; }
@media (prefers-reduced-motion: reduce) {
  .dtro-panel, .dtro-bubble { transition: none; }
}
@media (max-width: 480px) {
  .dtro-root:not(.inline) .dtro-panel {
    position: fixed; inset: 0; width: 100%; height: 100%;
    max-height: none; max-width: none; border-radius: 0; bottom: 0;
  }
}

.dtro-header {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 16px; border-bottom: 1px solid var(--dtro-border);
}
.dtro-header .dtro-title {
  font-family: var(--dtro-font-display); font-weight: var(--dtro-heading-weight);
  font-size: 15px; flex: 1;
}
.dtro-header .dtro-progress { color: var(--dtro-muted); font-size: 12px; }
.dtro-iconbtn {
  border: none; background: transparent; color: var(--dtro-muted);
  width: 32px; height: 32px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
}
.dtro-iconbtn:hover { background: var(--dtro-bubble-interviewer); }
.dtro-iconbtn svg { width: 18px; height: 18px; }

.dtro-log {
  flex: 1; overflow-y: auto; padding: 16px;
  display: flex; flex-direction: column; gap: 10px;
  font-size: 14px; line-height: 1.45;
}
.dtro-msg { max-width: 85%; padding: 9px 13px; border-radius: var(--dtro-radius-bubble);
  white-space: pre-wrap; overflow-wrap: break-word; }
.dtro-msg.interviewer {
  align-self: flex-start; background: var(--dtro-bubble-interviewer);
  color: var(--dtro-bubble-interviewer-fg);
}
.dtro-msg.participant {
  align-self: flex-end; background: var(--dtro-bubble-participant);
  color: var(--dtro-bubble-participant-fg);
}
.dtro-msg.pending { opacity: .75; }
.dtro-msg.system { align-self: center; color: var(--dtro-muted); font-size: 12px;
  background: transparent; padding: 0; }
.dtro-typing { align-self: flex-start; color: var(--dtro-muted); font-size: 12px; }

.dtro-images { display: flex; flex-wrap: wrap; gap: 8px; align-self: flex-start; }
.dtro-images figure { margin: 0; max-width: 45%; }
.dtro-images img { max-width: 100%; border-radius: 8px; display: block; }
.dtro-images figcaption { font-size: 11px; color: var(--dtro-muted); margin-top: 2px; }

.dtro-choices {
  padding: 0 16px 8px; display: flex; flex-wrap: wrap; gap: 8px;
  /* a long list takes most of the panel (the thread shrinks), then scrolls
     with a "Show more" bar rather than pushing the input row away */
  flex: 0 1 auto; min-height: 0; max-height: 70%; overflow-y: auto; overscroll-behavior: contain;
}
.dtro-log { min-height: 72px; }
/* zero-height sticky strip: toggling it never changes the scroll height */
.dtro-more { position: sticky; bottom: -8px; width: 100%; height: 0; display: none; }
.dtro-choices.clipped .dtro-more { display: block; }
.dtro-more button {
  position: absolute; left: -16px; right: -16px; bottom: 0;
  padding: 24px 0 6px; border: none; cursor: pointer; font: inherit; font-size: 12px; font-weight: 600;
  color: var(--dtro-accent); background: linear-gradient(to bottom, transparent, var(--dtro-bg) 55%);
}
.dtro-more button::after { content: " ↓"; }
.dtro-chip {
  border: 1px solid var(--dtro-accent); color: var(--dtro-accent);
  background: transparent; border-radius: var(--dtro-radius-pill); padding: 7px 14px; font-size: 13px;
}
.dtro-chip:hover, .dtro-chip.selected {
  background: var(--dtro-accent); color: var(--dtro-accent-contrast);
}
.dtro-chip.subtle { border-color: var(--dtro-border); color: var(--dtro-muted); }
.dtro-choices select {
  width: 100%; padding: 9px; border-radius: 8px;
  border: 1px solid var(--dtro-border); background: var(--dtro-bg); color: var(--dtro-fg);
}
.dtro-choices .dtro-hint { width: 100%; color: var(--dtro-muted); font-size: 12px; }

.dtro-inputrow {
  display: flex; gap: 8px; padding: 12px 16px;
  border-top: 1px solid var(--dtro-border); align-items: flex-end;
}
.dtro-inputrow textarea {
  flex: 1; resize: none; border: 1px solid var(--dtro-border);
  border-radius: 10px; padding: 9px 12px; font: inherit; font-size: 14px;
  background: var(--dtro-bg); color: var(--dtro-fg);
  max-height: 110px; min-height: 38px;
}
.dtro-send, .dtro-mic {
  border: none; border-radius: 10px; width: 40px; height: 38px; flex: none;
  background: var(--dtro-accent); color: var(--dtro-accent-contrast);
  display: flex; align-items: center; justify-content: center;
}
.dtro-send:disabled { opacity: .5; cursor: default; }
.dtro-mic.recording { background: #dc2626; animation: dtro-pulse 1.2s infinite; }
@keyframes dtro-pulse { 50% { box-shadow: 0 0 0 6px rgba(220,38,38,.25); } }
@media (prefers-reduced-motion: reduce) { .dtro-mic.recording { animation: none; } }
.dtro-send svg, .dtro-mic svg { width: 18px; height: 18px; }

/* ------------------------------------------------------------- splashes */
/* One layer over the thread — the study's welcome at the start, the ending at
   the finish — never over the header, so the panel can always be closed. A
   long welcome scrolls inside the layer rather than pushing its buttons off
   the bottom: the decision has to stay on screen next to what is being agreed
   to. */
.dtro-body { position: relative; flex: 1; display: flex; flex-direction: column; min-height: 0; }
.dtro-overlay {
  position: absolute; inset: 0; z-index: 1;
  background: var(--dtro-bg); color: var(--dtro-fg);
  display: flex; flex-direction: column; overflow-y: auto;
  padding: 20px 18px calc(20px + env(safe-area-inset-bottom));
}
.dtro-overlay__inner {
  margin: auto 0; display: flex; flex-direction: column; gap: 12px;
  width: 100%; max-width: 420px; margin-inline: auto;
}
.dtro-overlay.done .dtro-overlay__inner { text-align: center; align-items: center; }
.dtro-overlay__badge {
  width: 38px; height: 38px; border-radius: 50%;
  background: var(--dtro-accent); color: var(--dtro-accent-contrast);
  display: flex; align-items: center; justify-content: center; font-size: 19px;
}
.dtro-overlay__title {
  margin: 0; font-family: var(--dtro-font-display); font-size: 17px;
  font-weight: var(--dtro-heading-weight); line-height: 1.3;
}
.dtro-overlay__text {
  font-size: 14px; line-height: 1.5; white-space: pre-wrap; overflow-wrap: break-word;
}
.dtro-overlay.done .dtro-overlay__text { color: var(--dtro-muted); font-size: 13px; }
/* The voice start screen's study welcome (widget.ts voiceStartScreen): hero,
   time estimate, the message as rich text (src/richtext.ts), and a links-only
   footer — /voice's start screen at panel size. */
.dtro-overlay__hero {
  display: block; width: 100%; max-height: 140px; object-fit: contain;
  object-position: left center; border-radius: 10px;
}
.dtro-overlay__time { margin: -6px 0 0; font-size: 12px; color: var(--dtro-muted); }
.dtro-overlay__footer { font-size: 12px; line-height: 1.5; color: var(--dtro-muted); }
.dtro-rich { white-space: normal; }
.dtro-rich > :first-child { margin-top: 0; }
.dtro-rich > :last-child { margin-bottom: 0; }
.dtro-rich p, .dtro-rich ul, .dtro-rich ol { margin: 0 0 .7em; }
.dtro-rich ul, .dtro-rich ol { padding-left: 1.3em; }
.dtro-rich li + li { margin-top: .25em; }
.dtro-rich h3 { margin: 0 0 .45em; font-size: 1.05em; line-height: 1.3; }
.dtro-rich a { color: var(--dtro-accent); text-decoration: underline; text-underline-offset: 2px; }
.dtro-rich strong { font-weight: 700; }
/* The consent question the buttons answer, right above them. */
.dtro-overlay__prompt { margin: 0; font-size: 14px; font-weight: 600; line-height: 1.4; }
/* The visitor's language pick: top-right of the welcome, out of the way. */
.dtro-overlay__lang { align-self: flex-end; }
.dtro-overlay__lang select {
  font: inherit; font-size: 12px; color: var(--dtro-fg); background: var(--dtro-bg);
  border: 1px solid var(--dtro-border); border-radius: 999px; padding: 5px 10px; cursor: pointer;
}
.dtro-overlay__lang select:disabled { opacity: .55; cursor: default; }
.dtro-overlay__actions { display: flex; flex-direction: column; gap: 8px; width: 100%; }
/* The welcome's buttons: the decision, at full width, where a thumb is. */
.dtro-btn {
  border: 1px solid var(--dtro-accent); border-radius: 10px; padding: 11px 16px;
  font-size: 14px; font-weight: 600;
  background: var(--dtro-accent); color: var(--dtro-accent-contrast);
}
.dtro-btn.secondary { background: transparent; color: var(--dtro-accent); font-weight: 500; }
.dtro-btn:disabled { opacity: .55; cursor: default; }
/* Voice start screen: reads the study's welcome out. */
.dtro-listen {
  align-self: flex-start; display: inline-flex; align-items: center; gap: 6px;
  border: 1px solid var(--dtro-border); border-radius: 999px; padding: 7px 14px 7px 11px;
  font-size: 13px; font-weight: 600; color: var(--dtro-fg); background: var(--dtro-bg);
}
.dtro-listen svg { width: 16px; height: 16px; color: var(--dtro-accent); flex-shrink: 0; }
.dtro-listen:hover:not(:disabled), .dtro-listen:focus-visible { border-color: var(--dtro-accent); outline: none; }
.dtro-listen:disabled { opacity: .55; cursor: default; }
.dtro-listen[hidden] { display: none; }
/* Progress reads muted; only a genuine failure turns it red, so a healthy
   start doesn't flash an alarming line across the panel. */
.dtro-overlay__note { margin: 0; font-size: 12px; color: var(--dtro-muted); }
.dtro-overlay__note.error { color: #dc2626; }
.dtro-overlay__invite {
  margin-top: 4px; padding-top: 12px; border-top: 1px solid var(--dtro-border);
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  font-size: 13px; line-height: 1.45;
}
.dtro-overlay__invite p { margin: 0; }
.dtro-overlay__link { color: var(--dtro-accent); overflow-wrap: anywhere; }

.dtro-status { padding: 4px 16px 10px; color: var(--dtro-muted); font-size: 12px; }
/* Recording notice. A banner rather than a badge on the mic: the mic is
   captured on every voice interview to be transcribed, and only *this* means
   the audio is being kept. Stays up for the whole session, since the
   interviewer's audio is recorded too. */
.dtro-recnotice { display: flex; flex-direction: column; gap: 12px; width: 100%; text-align: left; }
.dtro-recnotice__title { margin: 0; font-size: 16px; font-weight: var(--dtro-heading-weight, 600); outline: none; }
.dtro-recnotice__required, .dtro-recnotice__hint { margin: 0; font-size: 13px; line-height: 1.45; }
.dtro-recnotice__hint { color: var(--dtro-muted); }
.dtro-recnotice__option { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; line-height: 1.45; }
.dtro-recnotice__option input { margin-top: 2px; }
.dtro-recnotice__actions { display: flex; flex-direction: column; gap: 8px; margin-top: 4px; }
.dtro-recbar {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 16px; font-size: 12px; line-height: 1.35;
  background: var(--dtro-accent-soft, rgba(80, 53, 255, .08));
  border-bottom: 1px solid var(--dtro-line);
}
.dtro-recbar::before {
  content: ""; flex: none; width: 8px; height: 8px; border-radius: 50%;
  background: #e5484d;
}
.dtro-recbar.broken { background: rgba(229, 72, 77, .12); }
.dtro-recbar.broken::before { background: var(--dtro-muted); }
/* No live recording session (idle): a still, muted dot. */
.dtro-recbar.paused::before { background: var(--dtro-muted); }
.dtro-powered {
  text-align: center; font-size: 10px; color: var(--dtro-muted);
  padding: 4px 0 8px;
}

/* the contact step's form (contact.ts) */
.dtro-contact { display: flex; flex-direction: column; gap: 10px; width: 100%; margin: 0; }
.dtro-contact-field { display: flex; flex-direction: column; gap: 4px; }
.dtro-contact-field label { font-size: 13px; font-weight: 600; color: var(--dtro-fg); }
.dtro-contact-optional { font-weight: 400; color: var(--dtro-muted); }
.dtro-contact input {
  font: inherit; font-size: 16px; min-height: 42px; padding: 8px 10px; box-sizing: border-box;
  width: 100%; color: var(--dtro-fg); background: var(--dtro-bg);
  border: 1px solid var(--dtro-border); border-radius: 10px;
}
.dtro-contact input:focus-visible { outline: 2px solid var(--dtro-accent); outline-offset: 1px; }
.dtro-contact input:user-invalid,
.dtro-contact input[aria-invalid="true"] { border-color: #dc2626; }
.dtro-contact-error, .dtro-contact-status { margin: 0; font-size: 12px; color: #dc2626; }
.dtro-contact-error::before { content: "⚠ "; }
.dtro-contact-status:empty { display: none; }
.dtro-contact-privacy { margin: 0; font-size: 12px; color: var(--dtro-muted); }
.dtro-contact-actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.dtro-contact-done { margin: 0; font-size: 13px; font-weight: 600; color: var(--dtro-muted); }
`;
